import axios, { AxiosError } from "axios";
import React from "react";
import { connect } from "react-redux";
import { generatePath } from "react-router-dom";
import {
  ActionList,
  DropdownInput,
  IOption,
  InputSizes,
  PaginationControl,
  PageHeader,
  Search,
  isSmallDeviceWidth,
  getErrorPopupTexts,
  RouterProps,
  withAccessControl,
} from "component-library";
import { AccountType } from "shared-types";
import { MapDispatchToProps } from "shared/types/utilities";
import {
  BREAKDOWN_FULL_PATH,
  JOB_UPDATE_NAVIGATION_DELAY,
  PAGINATION_PAGE_RANGE_DESKTOP,
  PAGINATION_PAGE_RANGE_MOBILE,
} from "config/common";
import PageKey from "config/PageKey";
import { IApplicationState } from "store";
import { hidePopup, showPopup } from "store/ui/ui.actions";
import { HidePopup, PopupType, ShowPopup } from "store/ui/ui.types";
import { IWithRequestAbortController } from "types/axios";
import {
  ClientAlertID,
  IClientAlert,
  IPaginatedClientAlertList,
} from "models/clientAlert";
import { IListItem } from "models/common";
import getErrorPopupType from "helpers/getErrorPopupType";
import suggestionsService from "services/api/suggestions/suggestionsService";
import {
  GetClientAlerts,
  IClientAlertsRequestParams,
  SetClientAlerts,
} from "services/api/suggestions/suggestionsService.types";

import {
  ClientAlertsFilterControl,
  IFilterValues,
} from "components/ClientAlertsFilterControl/ClientAlertsFilterControl";
import ClientAlertsView from "components/ClientAlertsView/ClientAlertsView";
import SelectAllControl from "components/SelectAllControl/SelectAllControl";
import PaginationResultStats from "components/PaginationResultStats/PaginationResultStats";
import RefreshButton from "components/RefreshButton/RefreshButton";

import { IJobsPageInjectedProps } from "../JobsPage";

import {
  clientAlertConverter,
  ClientAlertSortingType,
  getFilteringOptionsFomFilterValues,
  prepareClientAlertsTexts,
  sortingOptionsToSortFilterMapping,
} from "./ClientAlertsPage.helpers";

import {
  CLIENT_ALERTS_SEARCH,
  searchRefClear,
  persistSearch,
} from "shared/helpers/common";

import "./ClientAlertsPage.scss";

const SEARCH_MAX_LENGTH = 55;

export interface IClientAlertsPageTexts {
  dateTimeFormat: string;
  clientAlertTitleNudge: string;
  clientAlertTitleSuggestion: string;
  suggested: string;
  nudged: string;
  closingDateLabels: IListItem[];
  resultStats: string;
  selectAllAlertsLabel: string;
  markAsReadAlertsLabel: string;
  markAsUnreadAlertsLabel: string;
  searchPlaceholder: string;
  filterDateFilter: string;
  sentOnFormat: string;
}

export interface IClientAlertsPageStoreProps {
  locale: string;
  texts: IClientAlertsPageTexts;
  getClientAlerts: GetClientAlerts;
  markAsRead: SetClientAlerts;
  markAsUnread: SetClientAlerts;
}

export interface IClientAlertsPageActions {
  showPopup: ShowPopup;
  hidePopup: HidePopup;
}

export interface IClientAlertsPageProps
  extends
    IClientAlertsPageStoreProps,
    IClientAlertsPageActions,
    IJobsPageInjectedProps,
    RouterProps {}

export interface IClientAlertsPageState {
  selectedItems: ClientAlertID[];
  pageNumber: number;
  isLoading: boolean;
  paginatedClientAlerts: IPaginatedClientAlertList;
  sortingOption: ClientAlertSortingType;
  filterValues?: IFilterValues;
  search?: string;
}

export class ClientAlertsPage
  extends React.PureComponent<IClientAlertsPageProps, IClientAlertsPageState>
  implements IWithRequestAbortController
{
  public abortController = new AbortController();
  public static pageSize: number = 50;
  public static defaultPageNumber = 1;

  public readonly state: Readonly<IClientAlertsPageState> = {
    selectedItems: [],
    pageNumber: ClientAlertsPage.defaultPageNumber,
    isLoading: false,
    paginatedClientAlerts: {
      count: 0,
      items: [],
    },
    sortingOption: ClientAlertSortingType.DateSentDesc,
  };

  private readonly searchRef = React.createRef<Search>();

  public componentDidMount() {
    this.loadClientAlerts();
    this.initSearch();
  }

  public componentWillUnmount() {
    this.abortController.abort?.();
  }

  public render() {
    const { texts, locale, commonTexts } = this.props;
    const { pageNumber, selectedItems, paginatedClientAlerts, isLoading } =
      this.state;

    return (
      <>
        <PageHeader
          pageTitle={commonTexts.title}
          bottomSection={this.renderBottomSection()}
          actionSection={
            <ActionList variant="pageHeader" items={[]} moreButtonText={""} />
          }
        />
        <ClientAlertsFilterControl
          locale={locale}
          texts={texts}
          onValueChange={this.handleFilterValuesChange}
          paginationContent={this.renderPagination()}
        />
        <div className="g-content-area mt-1">
          <div className="g-col-lg-12">
            <div className="flex flex-col-reverse flex-wrap items-start justify-between gap-3 md:flex-row">
              <div className="flex flex-col gap-2">
                <PaginationResultStats
                  page={pageNumber}
                  itemsPerPage={ClientAlertsPage.pageSize}
                  itemsTotal={paginatedClientAlerts.count}
                  textTemplate={texts.resultStats}
                />
                <SelectAllControl
                  selectItems={this.selectItems}
                  deselectItems={this.deselectItems}
                  selectedItems={selectedItems}
                  markItemsAsRead={this.markSelectedItemsAsRead}
                  markItemsAsUnread={this.markSelectedItemsAsUnread}
                  items={paginatedClientAlerts.items}
                />
              </div>
              <div className="w-full md:flex md:w-auto md:justify-end md:gap-3">
                <div className="grid grid-cols-1 gap-3 md:contents">
                  <div className="min-w-0">{this.renderSorter()}</div>
                </div>
                <div className="mt-3 flex justify-end md:mt-0 md:contents">
                  <RefreshButton
                    onClick={this.handleRefresh}
                    className="!flex !aspect-square !h-10 !w-10 !items-center !justify-center !gap-[10px] !rounded-[10px] !border !border-grey-light-2"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="g-content-area">
          <div className="g-col-lg-12">
            <ClientAlertsView
              texts={texts}
              items={paginatedClientAlerts.items}
              selectedItems={selectedItems}
              deselectItems={this.deselectItems}
              selectItems={this.selectItems}
              onClientAlertClick={this.handleClientAlertClick}
              itemsPerPage={ClientAlertsPage.pageSize}
              isPageLoading={isLoading}
              pageNumber={pageNumber}
              itemsCount={paginatedClientAlerts.count}
              updatePageNumber={this.handlePageChange}
              onMarkAsRead={this.handleMarkItemsAsRead}
              onMarkAsUnread={this.handleMarkItemsAsUnread}
              showTopControls={false}
            />
          </div>
        </div>
      </>
    );
  }

  private renderBottomSection = () => {
    const { texts } = this.props;

    return (
      <div className="g-inner-grid-12 c-client-alerts-page__page-header-bottom-section">
        <div className="g-col-lg-6">
          <Search
            ref={this.searchRef}
            maxLength={SEARCH_MAX_LENGTH}
            onSearch={this.handleSearch}
            placeholder={texts.searchPlaceholder}
            onClear={this.handleSearchClear}
            initialValue={this.state.search ?? ""}
          />
        </div>
      </div>
    );
  };

  private renderSorter = () => {
    const { commonTexts } = this.props;

    return (
      <DropdownInput<string>
        id="clientAlertsSorting"
        size={InputSizes.Medium}
        className="[&_.c-input-wrapper]:!mb-0"
        variant="select"
        options={commonTexts.sortingOptions}
        selected={this.getSortingOption(this.state.sortingOption)}
        optionSelected={this.handleSortingChange}
      />
    );
  };

  private getSortingOption = (
    value: ClientAlertSortingType = ClientAlertSortingType.DateSentDesc
  ) => {
    const { sortingOptions } = this.props.commonTexts;

    return (
      sortingOptions.find((option) => option.value === value) ||
      sortingOptions[0]
    );
  };

  private handleSortingChange = (option: IOption<ClientAlertSortingType>) => {
    this.setState(
      {
        sortingOption: option.value,
        pageNumber: ClientAlertsPage.defaultPageNumber,
      },
      this.loadClientAlerts
    );
  };

  private handlePageChange = (pageNumber: number) => {
    this.setState({ pageNumber, selectedItems: [] }, this.loadClientAlerts);
  };

  private handleSearchClear = () => {
    searchRefClear(this.searchRef);
    this.handleSearch();
  };

  private handleSearch = (search: string = "") => {
    persistSearch(CLIENT_ALERTS_SEARCH, search);

    this.setState({ search }, this.loadClientAlerts);
  };

  private handleRefresh = () => {
    this.loadClientAlerts();
    this.props.refreshCounters();
  };

  private handleFilterValuesChange = (values: IFilterValues) => {
    if (JSON.stringify(this.state.filterValues) !== JSON.stringify(values)) {
      this.setState({ filterValues: values }, this.loadClientAlerts);
    }
  };

  private handleMarkItemsAsRead = async (clientAlertId: string[]) => {
    await this.toggleReadStatus(clientAlertId, this.props.markAsRead);
  };

  private handleMarkItemsAsUnread = async (clientAlertId: string[]) => {
    await this.toggleReadStatus(clientAlertId, this.props.markAsUnread);
  };

  private handleClientAlertClick = async (item: IClientAlert) => {
    const { router } = this.props;
    await this.props.markAsRead([item.id]);
    router.navigate(
      generatePath(BREAKDOWN_FULL_PATH, { breakdownId: item.breakdown.id })
    );
  };

  private markSelectedItemsAsRead = (clientAlertId: string[]) => {
    void this.handleMarkItemsAsRead(clientAlertId);
  };

  private markSelectedItemsAsUnread = (clientAlertId: string[]) => {
    void this.handleMarkItemsAsUnread(clientAlertId);
  };

  private handleError(e: AxiosError) {
    if (axios.isCancel(e)) {
      return;
    }

    this.props.showPopup({
      type: PopupType.ErrorPopup,
      traceID: e?.response?.headers?.["x-trace-identifier"],
      data: {
        ...getErrorPopupTexts()[getErrorPopupType()],
        hidePopup: this.props.hidePopup,
      },
    });
  }

  private selectItems = (clientAlertId: string[]) => {
    this.setState({
      selectedItems: [...this.state.selectedItems, ...clientAlertId],
    });
  };

  private deselectItems = (clientAlertId: string[]) => {
    this.setState({
      selectedItems: this.state.selectedItems.filter(
        (item) => !clientAlertId.includes(item)
      ),
    });
  };

  private readonly initSearch = () => {
    const savedSearch = sessionStorage.getItem(CLIENT_ALERTS_SEARCH);

    if (savedSearch) {
      this.setState({ search: savedSearch }, this.loadClientAlerts);

      return;
    }

    this.loadClientAlerts();
  };

  private async loadClientAlerts() {
    const { pageNumber, sortingOption, filterValues, search } = this.state;

    this.setState({ isLoading: true });
    this.abortController.abort?.();
    this.abortController = new AbortController();
    try {
      const params: IClientAlertsRequestParams = {
        skip: (pageNumber - 1) * ClientAlertsPage.pageSize,
        take: ClientAlertsPage.pageSize,
        sortOption: sortingOptionsToSortFilterMapping[sortingOption],
        filteringOptions: getFilteringOptionsFomFilterValues(filterValues),
        search,
      };
      const alerts = await this.props.getClientAlerts(
        params,
        this.abortController
      );

      this.setState({
        paginatedClientAlerts: {
          count: alerts.total ?? 0,
          items: alerts.items ? clientAlertConverter.convert(alerts.items) : [],
        },
      });
    } catch (e) {
      this.handleError(e);
    } finally {
      this.setState({ isLoading: false, selectedItems: [] });
    }
  }

  private renderPagination = () => {
    const { pageNumber, paginatedClientAlerts } = this.state;
    const pageCount = Math.ceil(
      paginatedClientAlerts.count / ClientAlertsPage.pageSize
    );

    return (
      <div className="ml-auto">
        <PaginationControl
          handlePageChange={this.handlePageChange}
          pageRangeDisplayed={
            isSmallDeviceWidth()
              ? PAGINATION_PAGE_RANGE_MOBILE
              : PAGINATION_PAGE_RANGE_DESKTOP
          }
          pageCount={pageCount}
          selectedPage={pageNumber}
          initialSelectedPage={pageNumber}
          arePageBreaksMandatory
        />
      </div>
    );
  };

  private async toggleReadStatus(
    clientAlertId: ClientAlertID[],
    handler: SetClientAlerts
  ) {
    this.setState({ isLoading: true });

    try {
      await handler(clientAlertId);
      setTimeout(() => {
        this.loadClientAlerts();
        this.props.refreshCounters();
      }, JOB_UPDATE_NAVIGATION_DELAY);
    } catch (e) {
      this.handleError(e);
      this.setState({ isLoading: false });
    }
  }
}

const mapStateToProps = (
  state: IApplicationState
): IClientAlertsPageStoreProps => {
  const pageTranslations = state.translation.pages[PageKey.Jobs]!;
  const commonTranslations = state.translation.common;

  return {
    locale: state.ui.locale,
    texts: prepareClientAlertsTexts(pageTranslations, commonTranslations),
    getClientAlerts: (...args) => suggestionsService.getClientAlerts(...args),
    markAsRead: (...args) => suggestionsService.markAsRead(...args),
    markAsUnread: (...args) => suggestionsService.markAsUnread(...args),
  };
};

const mapDispatchToProps: MapDispatchToProps<IClientAlertsPageActions> = {
  showPopup,
  hidePopup,
};

const ProtectedClientAlertsPage = withAccessControl<IClientAlertsPageProps>({
  accountTypes: [AccountType.Agent],
})(
  ClientAlertsPage as React.ComponentType<
    React.PropsWithChildren<IClientAlertsPageProps>
  >
);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ProtectedClientAlertsPage);
